import { useMemo } from "react";
import { useNavigation } from "@refinedev/core";
import type { ColumnDef } from "@tanstack/react-table";
import { Network, Plus } from "@/components/ui/icon";

import type { Skill } from "@aos/shared";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/app/StatusBadge";
import { formatDateTime } from "@/lib/format";
import { RowActions } from "@/resources/_shared/ListScaffold";
import { ResourceTable } from "./_components/ResourceTable";

/**
 * Skills list — the shared server-driven `ResourceTable` (PageHeader + table +
 * canonical states). A toolbar button links to the hierarchical SkillTree view.
 * No id column: the name leads, status/domain/department/version/updated follow.
 */
export function SkillList() {
  const { create, edit, show, push } = useNavigation();

  const columns = useMemo<ColumnDef<Skill>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Name",
        cell: ({ getValue }) => (
          <span className="font-medium">{getValue() as string}</span>
        ),
      },
      {
        id: "domain",
        accessorKey: "domain",
        header: "Domain",
        cell: ({ getValue }) => (getValue() as string | null) ?? "—",
      },
      {
        id: "department",
        accessorKey: "department",
        header: "Department",
        cell: ({ getValue }) => (getValue() as string | null) ?? "—",
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => <StatusBadge status={getValue() as string} />,
      },
      {
        id: "version",
        accessorKey: "version",
        header: "Version",
      },
      {
        id: "updated_at",
        accessorKey: "updated_at",
        header: "Updated",
        cell: ({ getValue }) => (
          <span className="text-muted-foreground">
            {formatDateTime(getValue() as string)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <RowActions
              onShow={() => show("skills", row.original.id)}
              onEdit={() => edit("skills", row.original.id)}
            />
          </div>
        ),
      },
    ],
    [show, edit],
  );

  return (
    <ResourceTable<Skill>
      resource="skills"
      title="Skills"
      subtitle="Reusable capability packages — contracts, body, and lifecycle."
      columns={columns}
      initialSort={{ field: "updated_at", order: "desc" }}
      emptyMessage="No skills yet."
      toolbar={
        <>
          <Button variant="outline" onClick={() => push("/skills/tree")}>
            <Network className="mr-2 h-4 w-4" />
            Skill Tree
          </Button>
          <Button onClick={() => create("skills")}>
            <Plus className="mr-2 h-4 w-4" />
            New Skill
          </Button>
        </>
      }
    />
  );
}

export default SkillList;
